<div class="device-search-container">
  <!-- Loading State -->
  <div *ngIf="isLoading" class="loading-state">
    <mat-progress-spinner
      diameter="80"
      mode="indeterminate"
      color="primary">
    </mat-progress-spinner>
    <p class="loading-text">Loading devices...</p>
  </div>

  <!--
    Cancel + Save Changes bar replaces the filter bar while edits are
    pending (`[hideFilterBar]="editBarVisible"` on the SSRM grid). The
    visible stack reads as: filter bar OR edit-mode bar — never both
    stacked. The "Show changes only" text-link toggle in the bar pushes
    `idAllowList$` to filter the grid to only edited rows server-side.
  -->
  <div
    *ngIf="editBarVisible"
    class="device-search-edit-bar"
  >
    <button
      type="button"
      class="device-search-edit-bar-cancel"
      (click)="onDiscardChanges()"
      [disabled]="isSaving"
    >
      Cancel
    </button>
    <a
      *ngIf="editChangeCount > 0"
      class="device-search-edit-bar-toggle"
      [class.is-active]="showChangesOnly"
      [class.is-disabled]="isSaving"
      role="button"
      tabindex="0"
      (click)="!isSaving && toggleShowChangesOnly()"
      (keydown.enter)="!isSaving && toggleShowChangesOnly()"
    >
      <span *ngIf="showChangesOnly" class="check" aria-hidden="true">✓</span>
      {{ showChangesOnly ? 'Showing' : 'Show' }}
      {{ editChangeCount }}
      {{ editChangeCount === 1 ? 'change' : 'changes' }}
    </a>
    <button
      mat-flat-button
      color="primary"
      type="button"
      class="device-search-edit-bar-save"
      (click)="onSaveChanges()"
      [disabled]="(editChangeCount === 0 && !isEditingDirty) || isSaving"
    >
      {{ isSaving ? 'Saving...' : 'Save changes' }}
    </button>
  </div>
  <!--
    Bulk-edit drawer wraps the SSRM grid (legacy nesting
    pattern). Drawer dispatches the bulk-update job and emits
    (bulkActionExecuted) with the resulting jobId; consumer's
    handleActionsExecuted creates the audit log + chains through
    the refreshRows for post-job grid refresh.
  -->
  <lib-cdw-bulk-update-action
    [class.hidden]="isLoading"
    [selectedItems]="selectedDeviceIds"
    [selectedCount]="(deviceGridStateService.selectedCount$ | async) ?? 0"
    [fieldUpdateConfigs]="bulkUpdateFieldConfigs"
    [visibleFields]="bulkEditActionConfig?.visibleFields"
    [defaultValues]="bulkEditActionConfig?.defaultValues"
    [actionType]="bulkEditActionConfig?.actionType"
    [crossPageReminderText]="bulkEditCrossPageReminder"
    [deprovisionReasons]="deprovisionReasons"
    [selectedDeviceData]="selectedDeviceData"
    [gridSessionId]="currentGridSessionId()"
    [customerId]="customerId"
    [userEmail]="userEmail"
    (bulkActionExecuted)="handleActionsExecuted($event)"
    (exportSkippedDevices)="onExportSkippedDevices($event)"
    bulkUpdateActionId="device-bulk-edit"
    drawerId="device-bulk-edit-drawer"
    drawerWidth="500px"
    drawerTitle="Bulk Edit Devices"
    drawerIcon="edit"
    toolbarTitleBackground="white"
    toolbarTitleColor="rgba(80, 82, 89, 0.8)"
    toolbarTitleFontFamily="Source Sans 3"
    toolbarTitleFontSize="20px"
    toolbarTitleFontWeight="800"
    [hideHeaderBorder]="true"
  >
  <!--
    SSRM stuck-job failsafe banner. Renders only when stuckJob$ emits a
    non-null execution (per `selectStuckJobsForGridSession`). The selector
    only flags jobs whose lastUpdated is older than `10min + 1min/5k assets`,
    AND only for the current grid session (per-tab UUID). Click cancels via
    the Optimistic cancelJob$ effect — local lock releases instantly, the
    cancelRunning API fires in the background.
  -->
  <app-stuck-job-banner
    [job]="stuckJob$ | async"
    (cancelClicked)="onCancelStuckJob($event)"
  ></app-stuck-job-banner>
  <lib-cdw-ssrm-grid
    data-testid="device-table"
    viewKey="device-search"
    [columnDefs]="columnDefs"
    [itemLabel]="itemLabel"
    [filterBarSettings]="filterBarSettings"
    [cdwSearchGridDataService]="devicesDataService"
    [componentId]="deviceSearchComponentId"
    [showPageSizeSelector]="true"
    [initialSelectedFilterFields]="currentFilters"
    [initialPage]="initialPage"
    [fieldMask$]="fieldMask$"
    [alwaysRequiredFields]="alwaysRequiredFields"
    [updatingIds$]="updatingIdsAsStringSet$"
    [selectionLockActive$]="lockActive$"
    [selectedRowSpinnerPredicate]="lockedSelectedSpinnerPredicate"
    [itemTotal$]="deviceGridStateService.itemTotal$"
    [selectedCount$]="deviceGridStateService.selectedCount$"
    [modifiedCellColor]="deviceModifiedCellColor"
    [singleClickEdit]="true"
    [showNonEditableTooltips]="true"
    [hideFilterBar]="editBarVisible"
    [savingCells$]="savingCells$"
    [pendingEdits$]="cellChanges$"
    [showSelectedOnly]="showSelectedOnly"
    [useSelectionAsFilter]="showSelectedOnly"
    [idAllowList$]="idAllowList$"
    [selectByFilter]="selectByFilterCallback"
    [deselectByFilter]="deselectByFilterCallback"
    [selectByIds]="selectByIdsCallback"
    [deselectByIds]="deselectByIdsCallback"
    [clearAllSelected]="clearAllSelectedCallback"
    (filtersChange)="onDeviceFiltersChanged($event)"
    (cellClicked)="onCellClicked($event)"
    (dataLoaded)="onDataLoaded($event)"
    (selectionToggled)="onSelectionToggled($event)"
    (selectionCount)="onSelectionCountChange($event)"
    (cellValueChanged)="onCellValueChanged($event)"
    (cellUndoClicked)="onCellUndoClicked($event)"
    (cellEditingStarted)="onCellEditingStarted()"
    (cellEditingStopped)="onCellEditingStopped()"
    (cellEditingDirtyChanged)="isEditingDirty = $event"
    (showSelectedOnlyChange)="onShowSelectedOnlyChange($event)"
    (errorRetrievingData)="onGridLoadError($event)"
    #deviceSearchGrid
  ></lib-cdw-ssrm-grid>
  </lib-cdw-bulk-update-action>
</div>
